import { Button, ButtonProps, cx, Flex, IconX, Text } from '@vtex/admin-ui'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { scrollToFirstError } from 'shared'
import type { DefaultProps } from 'typings'
import styles, { transitionDurationMs } from './drawer.css'
import { messages } from './messages'
import type { UseDrawerStateReturn } from './useDrawerState'

interface Props extends DefaultProps {
  state: UseDrawerStateReturn
  level?: number
}

type DrawerProps = DefaultProps & {
  className?: string
}

const ELEMENTS = ['input', 'select', 'textarea']

export function Drawer(props: Props) {
  const drawerRef = useRef<HTMLDialogElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const timeout = useRef<NodeJS.Timeout>()

  const {
    state: { open, hide },
    children,
    level = 0,
  } = props

  const [visible, setVisible] = useState(open)

  useEffect(() => {
    clearTimeout(timeout.current)

    if (open) {
      setVisible(true)
    } else {
      timeout.current = setTimeout(
        () => setVisible(false),
        transitionDurationMs
      )
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const handleOutsideClick = (event: Event) => {
      if (!containerRef.current) return
      const bg = containerRef.current.querySelector('[data-pp-drawer-bg]')
      if (bg?.contains(event.target as Element)) {
        hide()
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [hide, open])

  useEffect(() => {
    if (!open) return

    const handleEscPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hide()
      }
    }

    document.addEventListener('keydown', handleEscPress, true)

    return () => {
      document.removeEventListener('keydown', handleEscPress, true)
    }
  }, [hide, open])

  useEffect(() => {
    if (!visible || !open) return

    setTimeout(() => {
      const focusableElements: NodeListOf<HTMLElement> | undefined =
        drawerRef.current?.querySelectorAll(
          ELEMENTS.map(
            (element) => `${element}:not([disabled]):not([hidden])`
          ).join(',')
        )

      focusableElements?.length
        ? focusableElements[0].focus()
        : drawerRef.current?.focus()
    }, transitionDurationMs)
  }, [open, visible])

  return createPortal(
    <Flex align="flex-start" ref={containerRef}>
      <div
        data-pp-drawer-bg
        className={styles.drawerBg}
        style={{ display: visible ? 'block' : 'none' }}
      />
      <dialog
        className={cx(styles.drawer, `level-${level}`)}
        ref={drawerRef}
        open={open}
      >
        {visible && children}
      </dialog>
    </Flex>,
    document.body
  )
}

export function DrawerHeader(props: DrawerProps) {
  return (
    <div className={cx(styles.header, props.className ?? '')}>
      {props.children}
    </div>
  )
}

export function DrawerTitle(props: DrawerProps) {
  return (
    <Text variant="display" className={cx(styles.title, props.className ?? '')}>
      {props.children}
    </Text>
  )
}

export function DrawerSubtitle(props: DrawerProps) {
  return (
    <div className={cx(styles.subtitle, props.className ?? '')}>
      <Text variant="body" tone="secondary">
        {props.children}
      </Text>
    </div>
  )
}

export function DrawerFooter(props: DrawerProps) {
  return (
    <Flex className={cx(styles.footer, props.className ?? '')}>
      {props.children}
    </Flex>
  )
}

export function DrawerContent(props: DrawerProps) {
  return (
    <div className={cx(styles.content, props.className ?? '')}>
      {props.children}
    </div>
  )
}

interface DismissProps extends DrawerProps {
  onHide: () => void
}

export function DismissButton(props: DismissProps) {
  const { onHide } = props
  const { formatMessage: i18n } = useIntl()

  return (
    <Button
      variant="neutralTertiary"
      icon={<IconX />}
      onClick={onHide}
      aria-label={i18n({ id: 'admin/promotions.utils.close' })}
      className={cx(styles.closeButton, props.className ?? '')}
    />
  )
}

interface DrawerButtonProps extends ButtonProps {
  onClick: () => void
  isCreating?: boolean
}

export function CancelButton(props: DrawerButtonProps) {
  const { onClick, ...rest } = props

  return (
    <Button variant="tertiary" onClick={onClick} {...rest}>
      <FormattedMessage {...messages.button.cancel} />
    </Button>
  )
}

export function SaveButton(props: DrawerButtonProps) {
  const { onClick, isCreating = false, ...rest } = props

  const message = isCreating ? messages.button.add : messages.button.apply

  const handleClick = async () => {
    await onClick()
    scrollToFirstError({ target: 'dialog', behavior: 'smooth' })
  }

  const handleKeyDown: React.KeyboardEventHandler = (event) => {
    if (event.key === 'Enter') onClick()
  }

  return (
    <Button
      variant="primary"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      <FormattedMessage {...message} />
    </Button>
  )
}
